"use client";
import { useRouter, usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/scrollbar";
import { Scrollbar, Autoplay } from "swiper/modules";
import Spinner from "@/components/common/Spinner";
import SwiperClass from "swiper/types/swiper-class";

interface BannerItem {
  imageUrl: string;
  imageName?: string;
  hyperlink: string | null;
  isNewWindow: boolean | null;
}

interface BannerProps {
  data: unknown[];
  enableAutoplay?: boolean;
  autoplayDelay?: number;
  priority?: boolean;
}

const Banner = ({ data, enableAutoplay = false, autoplayDelay = 4000, priority = false }: BannerProps) => {
  const router = useRouter();
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);

  const handleImageClick = (url: string | null, isNewWindow: boolean | null) => {
    if (url) {
      if (isNewWindow) {
        window.open(url, "_blank");
      } else {
        router.push(url);
      }
    }
  };

  const modules = enableAutoplay ? [Scrollbar, Autoplay] : [Scrollbar];

  // Start autoplay when swiper instance is ready
  useEffect(() => {
    if (swiperInstance && enableAutoplay) {
      // 약간의 지연 후 autoplay 시작
      setTimeout(() => {
        if (swiperInstance.autoplay) {
          swiperInstance.autoplay.start();
        }
      }, 100);
    }
  }, [swiperInstance, enableAutoplay]);

  if (!data || data.length === 0) {
    return (
      <div className="w-full aspect-[16/9] mobile:aspect-[830/965] flex items-center justify-center bg-[#f5f5f5]">
        <Spinner size={60} />
      </div>
    );
  }

  return (
    <>
      <Swiper 
        scrollbar={true} 
        modules={modules}
        autoplay={enableAutoplay ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          stopOnLastSlide: false,
          waitForTransition: true,
        } : false}
        loop={enableAutoplay}
        onSwiper={(swiper) => setSwiperInstance(swiper)}
        onInit={(swiper) => {
          // 초기화 후 지연 시간을 두고 autoplay 시작
          if (enableAutoplay) {
            setTimeout(() => {
              if (swiper.autoplay) {
                swiper.autoplay.start();
              }
            }, autoplayDelay); // 첫 슬라이드는 설정된 딜레이만큼 보여줌
          }
        }}>
        {(data as BannerItem[])?.map((item, index: number) => (
          <SwiperSlide key={index}>
            <div className="relative cursor-pointer" onClick={() => handleImageClick(item.hyperlink, item.isNewWindow)}>
              <Image
                className="w-full max-h-[1080px] aspect-[16/9] mobile:aspect-[830/965] object-cover"
                src={item.imageUrl}
                alt={item.imageName || `Slide ${index + 1}`}
                width={1920}
                height={1080}
                sizes="100vw"
                priority={priority && index === 0}
              />
              {/* <div className="absolute top-0 w-full h-full flex flex-col justify-center text-white px-[340px]">
                <div className="text-[24px] font-[500] mt-[150px] mb-[36px]">
                  <div className="mb-[16px]">행복의 이상이 실현되는 공간</div>
                  <div>인터뷰멘션</div>
                </div>
                <div className="text-[32px] font-[600]">20% PROMOTION</div>
              </div> */}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default Banner;